import logger from '@greenlight/logger';
import get from 'lodash/get';
import { Analytics } from '../../ModularReduxAnalytics';
import { storageAvailable } from '../../helpers';
import InvestService from '../../services/invest/index.ts';
import datadogRumClient from '../../utils/datadog-rum';

const trackVerifyFundingSource = ({ eventProps }) =>
  Analytics.trackEvent('Funding Source Verified', {
    eventProps,
    delaySegmentTrackEvent: false
  });

const trackAddFundingSource = ({ eventProps }) =>
  Analytics.trackEvent('Funding Source Added', {
    eventProps,
    delaySegmentTrackEvent: false
  });

const trackLTAExperienceStarted = ({ action }) => {
  if (
    action.payload &&
    action.payload.nextQuestions &&
    action.payload.nextQuestions.length > 0
  ) {
    if (action.payload.nextQuestions.indexOf('set_user_email') !== -1) {
      Analytics.trackEvent('Experience Started', {
        eventProps: {
          experience_id: 'registration'
        },
        delaySegmentTrackEvent: false
      });
    }
    if (action.payload.nextQuestions.indexOf('set_plan') !== -1) {
      Analytics.trackEvent('Experience Started', {
        eventProps: {
          experience_id: 'plan_selection'
        },
        delaySegmentTrackEvent: false
      });
    }
    if (action.payload.nextQuestions.indexOf('add_child_info') !== -1) {
      Analytics.trackEvent('Experience Started', {
        eventProps: {
          experience_id: 'add_children'
        }
      });
    }
    if (action.payload.nextQuestions.indexOf('set_legal_first_name') !== -1) {
      Analytics.trackEvent('Experience Started', {
        eventProps: {
          experience_id: 'identity_verification'
        },
        delaySegmentTrackEvent: false
      });
    }
    if (
      action.payload.nextQuestions.indexOf('set_initial_funding_amount') !== -1
    ) {
      Analytics.trackEvent('Experience Started', {
        eventProps: {
          experience_id: 'funding'
        },
        delaySegmentTrackEvent: false
      });
    }
  }
};

const mapper = (action, store) => {
  let checkPhoneError;

  let errorActions = [];

  switch (action.type) {
    case 'userCheckPhoneSuccess':
    case 'appCheckPhoneSuccess':
      if (action.payload && action.payload.action === 'confirm-role') {
        Analytics.trackEvent('WebReg Flow Started');
        Analytics.trackEvent('Onboarding Flow Started');
        Analytics.trackEvent('Onboarding Flow Selected', {
          eventProps: {
            flow_id: 'DEFAULT'
          }
        });
      }
      break;
    case 'appCheckPhoneFailure':
      if (action.payload.response) {
        checkPhoneError = action.payload.response.data;
      }
      if (window.FS && typeof window.FS.event === `function`) {
        window.FS.event('Check Phone Failed', {
          error: checkPhoneError
        });
      }
      break;
    case 'appLoginSuccess':
      Analytics.loginUser(action.payload.user);
      break;

    case 'appSaveManualAchSuccess':
    case 'appAnswerSuccess':
      trackLTAExperienceStarted({ action });
      if (
        !!action.payload.unresolvedErrors &&
        action.payload.unresolvedErrors.length > 0
      ) {
        errorActions = action.payload.unresolvedErrors.map(
          unresolvedError => unresolvedError.action
        );
        for (let i = 0; i < action.payload.unresolvedErrors.length; i += 1) {
          const error = action.payload.unresolvedErrors[i];
          if (error.code !== 'ERR_KYC_SUCCESS') {
            Analytics.trackEvent('Onboarding Flow Step Error Triggered', {
              eventProps: {
                error_code: error.code,
                flow_id: error.flowId,
                flow_step: error.groupId
              },
              delaySegmentTrackEvent: false
            });
          }
          if (error.action === 'set_instant_funding_account') {
            logger.error(error.message);

            Analytics.trackEvent('Add Debit Card Failed', {
              eventProps: {
                error_code: error.code,
                error_message: error.message,
                during_registration: true
              },
              delaySegmentTrackEvent: false
            });
          }
        }
      }
      if (!!action.payload.extras && action.payload.extras.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const extra of action.payload.extras) {
          // For LTA recreation in legacy stack, we assume that if there are no errors,
          // then the user has resolved it. Additional Error events will be generated
          // If they need to be
          if (
            action.payload.unresolvedErrors &&
            action.payload.unresolvedErrors.length === 0 &&
            store.getState().app.unresolvedErrors &&
            store.getState().app.unresolvedErrors.length > 0
          ) {
            Analytics.trackEvent('Onboarding Flow Step Error Resolved', {
              eventProps: {
                flow_id: action.payload.flowId,
                flow_step: action.payload.groupId
              },
              delaySegmentTrackEvent: false
            });
          }
          Analytics.trackEvent('Onboarding Flow Step Submitted', {
            eventProps: {
              flow_id: action.payload.flowId,
              flow_step: action.payload.groupId
            },
            delaySegmentTrackEvent: false
          });

          if (
            extra.action === 'create_user' &&
            !errorActions.find(a => a === 'create_user')
          ) {
            Analytics.registerUser(
              extra.user,
              action.payload.flowId,
              store.getState().app.smsNotificationsOptedStatus
            );
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'registration'
              }
            });
          }
          if (
            extra.action === 'set_plan' &&
            !errorActions.find(a => a === 'set_plan')
          ) {
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'plan_selection'
              }
            });
          }
          if (
            extra.action === 'add_child' &&
            !errorActions.find(a => a === 'add_child')
          ) {
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'add_children'
              }
            });
          }
          if (
            extra.action === 'verify_identity' &&
            !errorActions.find(a => a === 'verify_identity')
          ) {
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'identity_verification'
              }
            });
          }
          if (
            extra.action === 'set_plaid_bank_account' &&
            !errorActions.find(a => a === 'set_plaid_bank_account')
          ) {
            const eventProps = {
              during_registration: true,
              funding_type: 'instant_ach'
            };
            trackAddFundingSource({ eventProps });
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'funding'
              }
            });
            trackVerifyFundingSource({ eventProps });
          }
          if (
            extra.action === 'set_instant_funding_account' &&
            !errorActions.find(a => a === 'set_instant_funding_account')
          ) {
            trackAddFundingSource({
              eventProps: {
                during_registration: true,
                funding_type: 'debit'
              }
            });
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'funding'
              }
            });
            const fundingInstrumentByType = {
              android_pay_card: 'googlePay',
              apple_pay_card: 'applePay'
            };
            const fundingType = get(extra, 'funding.type', '');

            Analytics.trackEvent('Debit Card Added', {
              eventProps: {
                during_registration: true,
                funding_type: 'debit',
                funding_instrument:
                  fundingInstrumentByType[fundingType] || 'card'
              }
            });

            if (!extra.needsVerify) {
              trackVerifyFundingSource({
                eventProps: {
                  during_registration: true,
                  funding_type: 'debit',
                  funding_instrument:
                    fundingInstrumentByType[fundingType] || 'card',
                  role: 'parent'
                }
              });
            }
          }
          if (
            extra.action === 'set_manual_bank_account' &&
            !errorActions.find(a => a === 'set_manual_bank_account')
          ) {
            trackAddFundingSource({
              eventProps: {
                during_registration: true,
                funding_type: 'manual_ach'
              }
            });
            Analytics.trackEvent('Experience Completed', {
              eventProps: {
                experience_id: 'funding'
              }
            });
          }
        }
      }

      if (
        action.payload.extras &&
        action.payload.extras[0]?.action === 'verify_identity' &&
        get(store.getState(), 'app.answers.setPlan.pricingPlanName') &&
        store.getState().app.answers.setPlan.pricingPlanName !== 'select'
      ) {
        Analytics.trackEvent('T&C Accepted', {
          eventProps: {
            during_registration: true,
            gl_product: 'Invest'
          }
        });

        InvestService.postAcceptTermsAndConditions({
          deviceId: store.getState().user.deviceToken,
          authToken: store.getState().app.stargatewayKey
        });
      }

      break;

    case 'appSavePlaidAccountSuccess':
      trackAddFundingSource({
        eventProps: {
          during_registration: true,
          funding_type: 'instant_ach'
        }
      });

      trackVerifyFundingSource({
        eventProps: {
          during_registration: true,
          role: action.payload.user.role,
          funding_type: 'instant_ach'
        }
      });
      break;

    case 'appSaveAchAccountSuccess':
      trackAddFundingSource({
        eventProps: {
          during_registration: true,
          funding_type: 'manual_ach'
        }
      });
      break;

    case 'userCompleteSuccess':
      Analytics.registrationComplete(
        action.payload.extras[0].user,
        store.getState().app.answers
      );
      Analytics.updateFBIdentity(action.payload.extras[0].user);
      if (storageAvailable('sessionStorage')) {
        sessionStorage.setItem('userCompleted', true);
      }
      Analytics.trackEvent('Onboarding Flow Completed', {
        eventProps: {
          flow_id: store.getState().app.flowId
        }
      });
      break;

    case 'appVerifyDebitNowSuccess':
      Analytics.trackBankAccountVerifiedEvent('debit');
      trackVerifyFundingSource({
        eventProps: {
          during_registration: true,
          funding_type: 'debit'
        }
      });
      break;

    case 'appVerifyDebitNowFailure':
      let maxAttempts = false;
      if (
        action.payload.response &&
        action.payload.response.data &&
        action.payload.response.data.code ===
          'ERR_MAX_CHALLENGE_WITHDRAWAL_VERIFICATIONS'
      ) {
        maxAttempts = true;
      }
      Analytics.trackEvent('Verify Debit Card Failed', {
        eventProps: {
          maxAttempts,
          during_registration: true,
          // debit_failed_verifications in an incrementing property. When this event is tracked it will increase by 1.
          debit_failed_verifications: 1
        }
      });

      break;

    case 'appAnswerFailure':
      if (
        action.payload.response &&
        action.payload.response.data &&
        Array.isArray(action.payload.response.data.message)
      ) {
        logger.error(action.payload.response.data.message);
      }
      break;

    case 'appInitializeSuccess':
      trackLTAExperienceStarted({ action });
      Analytics.identifyUser({ flowId: action.payload.flowId });
      break;

    case 'setRole':
      Analytics.trackEvent('Role Selected', {
        eventProps: {
          role: action.payload
        }
      });
      Analytics.trackEvent('Onboarding Intent Added', {
        eventProps: {
          intent:
            action.payload === 'parent'
              ? 'PARENT_STANDALONE'
              : 'CHILD_STANDALONE'
        }
      });
      // sessionRole is named differently from role, to allow for 2 distinct dd facets.
      // role: role property for the custom action.
      // sessionRole: role property for the user session.
      datadogRumClient.addRumGlobalContext('sessionRole', action.payload);
      break;

    default:
      break;
  }
};

const AnalyticsMiddleware = store => next => action => {
  mapper(action, store);
  next(action);
};
export default AnalyticsMiddleware;
